import React, { useState, useCallback } from 'react'
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import _debounce from 'lodash/debounce'
import { useRfqSearchQuery } from 'services/hooks/rfq/useRfqSearchQuery'
import { FilterSelect } from 'components/listTableFilterBox'
import { useRfqAssignUsersQuery } from 'services/hooks/rfq/useRfqAssignUsersQuery'
import { useSuppliersQuery } from 'services/hooks/supplier/useSuppliersQuery'
import { useRfqStatusListQuery } from 'services/hooks/rfq/useRfqStatusListQuery'

const DEBOUNCE_TIME_MS = 1000

type AutocompleteSearchProps = {
  onChangeFilter: (target: string, value: string) => void
  filterValues: {
    statuses: string[]
    users: string[]
    term: string
    suppliers: string[]
  }
}

const Filters = ({ onChangeFilter, filterValues }: AutocompleteSearchProps) => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [term, setTerm] = useState('')
  const { data: rfqSearchResult, isFetching } = useRfqSearchQuery({
    term,
    assignee: filterValues.users.join(),
    status: filterValues.statuses.join(),
    suppliers: filterValues.suppliers.join(),
  })
  const { data: rfqAssignUsersList } = useRfqAssignUsersQuery()
  const { data: suppliersList } = useSuppliersQuery()
  const { data: statusList } = useRfqStatusListQuery()

  const debouncedUpdateData = useCallback(
    _debounce((value: string) => {
      setTerm(value)
    }, DEBOUNCE_TIME_MS),
    []
  )

  const onChangeSearchField = async (e) => {
    setSearchValue(e.target.value)
    debouncedUpdateData(e.target.value)
  }

  const onChangeValue = (event, value) => {
    onChangeFilter('term', value)
  }

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Autocomplete
        id='asynchronous-demo'
        style={{ width: 200 }}
        open={open}
        onOpen={() => {
          setOpen(true)
        }}
        onClose={() => {
          setOpen(false)
        }}
        options={rfqSearchResult?.search.terms || []}
        loading={isFetching}
        onChange={onChangeValue}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Search'
            variant='outlined'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isFetching ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            value={searchValue}
            onChange={onChangeSearchField}
          />
        )}
      />
      <Typography>Filter By:</Typography>
      {rfqAssignUsersList && (
        <FilterSelect
          label='Assignee'
          filterType='users'
          filterList={rfqAssignUsersList}
          selectValues={filterValues.users}
          onChange={onChangeFilter}
        />
      )}
      {suppliersList && (
        <FilterSelect
          label='Supplier'
          filterType='suppliers'
          filterList={suppliersList}
          selectValues={filterValues.suppliers}
          onChange={onChangeFilter}
        />
      )}
      {statusList && (
        <FilterSelect
          label='Status'
          filterType='statuses'
          filterList={statusList}
          selectValues={filterValues.statuses}
          onChange={onChangeFilter}
        />
      )}
    </Box>
  )
}

export default Filters
